import React from "react";
import {
  effortTypeForSamplingMethod,
  isAnglingSamplingMethod,
  computeElectrofishingCpue,
  computeNetCpue,
  formatHMS,
} from "../../utils/samplingCpueUtils";
import { displayMethodLabel, LABEL } from "../../utils/samplingDisplayLabels";

const MODERN_TABLE_CLASS = "table table-sm table-bordered align-middle mb-0";
const MODERN_TABLE_HEAD_CLASS = "table-light";
const MODERN_TABLE_HEAD_STYLE = { fontSize: "0.8rem" };
const MODERN_TABLE_CELL_STYLE = { fontSize: "0.8rem", padding: "0.35rem 0.5rem" };

function ElectrofishingTable({ effortUnits, samplingData, speciesName, title }) {
  const { rows, totalHours, totalFish, avgCpue } = computeElectrofishingCpue(
    effortUnits,
    samplingData,
    speciesName
  );
  if (!rows.length) return null;

  return (
    <div className="mb-3">
      {title && <h6 className="mt-3 mb-2">{title}</h6>}
      <div className="table-responsive">
        <table className={MODERN_TABLE_CLASS}>
          <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
            <tr>
              <th style={MODERN_TABLE_CELL_STYLE}>Area</th>
              <th style={MODERN_TABLE_CELL_STYLE}>Total Time (hr:min:sec)</th>
              <th style={MODERN_TABLE_CELL_STYLE}>{LABEL.NUM}</th>
              <th style={MODERN_TABLE_CELL_STYLE}>CPUE</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td style={MODERN_TABLE_CELL_STYLE}>{row.label}</td>
                <td style={MODERN_TABLE_CELL_STYLE}>{row.timeDisplay}</td>
                <td style={MODERN_TABLE_CELL_STYLE}>{row.fishCount}</td>
                <td style={MODERN_TABLE_CELL_STYLE}>{row.cpue}</td>
              </tr>
            ))}
            <tr className="fw-semibold">
              <td style={MODERN_TABLE_CELL_STYLE}>Average CPUE</td>
              <td style={MODERN_TABLE_CELL_STYLE}>{totalHours.toFixed(3)}</td>
              <td style={MODERN_TABLE_CELL_STYLE}>{totalFish}</td>
              <td style={MODERN_TABLE_CELL_STYLE}>{avgCpue}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NetCpueTable({ effortUnits, samplingData, effortType, speciesName, title }) {
  const { rows, totalEffort, totalFish, avgCpue, isSeine } = computeNetCpue(
    effortUnits,
    samplingData,
    effortType,
    speciesName
  );
  if (!rows.length) return null;

  const isFykeOrGill = effortType === "fyke_net" || effortType === "gillnet";

  return (
    <div className="mb-3">
      {title && <h6 className="mt-3 mb-2">{title}</h6>}
      <div className="table-responsive">
        <table className={MODERN_TABLE_CLASS}>
          <thead className={MODERN_TABLE_HEAD_CLASS} style={MODERN_TABLE_HEAD_STYLE}>
            <tr>
              <th style={MODERN_TABLE_CELL_STYLE}>
                {isSeine ? "Haul #" : "Net"}
              </th>
              {isFykeOrGill && (
                <th style={MODERN_TABLE_CELL_STYLE}>Number of Nights</th>
              )}
              <th style={MODERN_TABLE_CELL_STYLE}>{LABEL.NUM}</th>
              <th style={MODERN_TABLE_CELL_STYLE}>CPUE</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td style={MODERN_TABLE_CELL_STYLE}>{row.label}</td>
                {isFykeOrGill && (
                  <td style={MODERN_TABLE_CELL_STYLE}>{row.effortLabel}</td>
                )}
                <td style={MODERN_TABLE_CELL_STYLE}>{row.fishCount}</td>
                <td style={MODERN_TABLE_CELL_STYLE}>{row.cpue}</td>
              </tr>
            ))}
            <tr className="fw-semibold">
              <td style={MODERN_TABLE_CELL_STYLE}>Average CPUE</td>
              {isFykeOrGill && (
                <td style={MODERN_TABLE_CELL_STYLE}>{totalEffort}</td>
              )}
              <td style={MODERN_TABLE_CELL_STYLE}>{totalFish}</td>
              <td style={MODERN_TABLE_CELL_STYLE}>{avgCpue}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MissingEffortUnitsNotice({ method }) {
  const effortType = effortTypeForSamplingMethod(method);
  if (!effortType) return null;

  return (
    <div className="alert alert-secondary py-2 px-3 small mb-3" role="status">
      CPUE unavailable — no effort areas or nets defined for this session. Check
      the General tab.
    </div>
  );
}

export function SessionCpueTables({ detail }) {
  const method = detail?.SamplingMethod;
  const effortUnits = detail?.EffortUnits || [];
  const samplingData = detail?.SamplingData || [];
  const effortType = effortTypeForSamplingMethod(method);

  if (!effortType) return null;

  if (!effortUnits.length) {
    return <MissingEffortUnitsNotice method={method} />;
  }

  if (effortType === "electrofishing_area") {
    return (
      <ElectrofishingTable
        effortUnits={effortUnits}
        samplingData={samplingData}
        title={`Catch per Unit Effort — ${displayMethodLabel("Electrofishing")}`}
      />
    );
  }

  const netTitle =
    effortType === "seine_haul"
      ? "Catch per Unit Effort — Seine"
      : effortType === "fyke_net"
        ? "Catch per Unit Effort — Fyke Net"
        : "Catch per Unit Effort — Gillnet";

  return (
    <NetCpueTable
      effortUnits={effortUnits}
      samplingData={samplingData}
      effortType={effortType}
      title={netTitle}
    />
  );
}

export function PerSpeciesCpueTables({ detail, fishTypeData }) {
  const method = detail?.SamplingMethod;
  const effortUnits = detail?.EffortUnits || [];
  const samplingData = detail?.SamplingData || [];
  const effortType = effortTypeForSamplingMethod(method);

  if (!effortType || !effortUnits.length || !fishTypeData?.length) return null;

  const speciesWithFish = fishTypeData.filter((s) => Number(s.NumberOfFishes) > 0);
  if (!speciesWithFish.length) return null;

  return (
    <>
      <h5 className="mt-4">CPUE by Species</h5>
      {speciesWithFish.map((summary) => {
        const speciesName = summary.FishTypeName || "Unknown";
        if (effortType === "electrofishing_area") {
          return (
            <ElectrofishingTable
              key={speciesName}
              effortUnits={effortUnits}
              samplingData={samplingData}
              speciesName={speciesName}
              title={speciesName}
            />
          );
        }
        return (
          <NetCpueTable
            key={speciesName}
            effortUnits={effortUnits}
            samplingData={samplingData}
            effortType={effortType}
            speciesName={speciesName}
            title={speciesName}
          />
        );
      })}
    </>
  );
}

export function shouldShowCpueForMethod(method) {
  return effortTypeForSamplingMethod(method) != null || isAnglingSamplingMethod(method);
}

function cpueSessionTitle(effortType) {
  if (effortType === "electrofishing_area") {
    return `Catch per Unit Effort — ${displayMethodLabel("Electrofishing")}`;
  }
  if (effortType === "seine_haul") return "Catch per Unit Effort — Seine";
  if (effortType === "fyke_net") return "Catch per Unit Effort — Fyke Net";
  return "Catch per Unit Effort — Gillnet";
}

function buildElectrofishingExportBlock(
  effortUnits,
  samplingData,
  speciesName = null,
  title = null
) {
  const { rows, totalHours, totalFish, avgCpue } = computeElectrofishingCpue(
    effortUnits,
    samplingData,
    speciesName
  );
  if (!rows.length) return null;

  return {
    title: title || cpueSessionTitle("electrofishing_area"),
    headers: ["Area", "Total Time (hr:min:sec)", LABEL.NUM, "CPUE"],
    bodyRows: rows.map((row) => [
      row.label,
      row.timeDisplay,
      String(row.fishCount),
      String(row.cpue),
    ]),
    footerRow: [
      "Average CPUE",
      totalHours.toFixed(3),
      String(totalFish),
      String(avgCpue),
    ],
  };
}

function buildNetCpueExportBlock(
  effortUnits,
  samplingData,
  effortType,
  speciesName = null,
  title = null
) {
  const { rows, totalEffort, totalFish, avgCpue, isSeine } = computeNetCpue(
    effortUnits,
    samplingData,
    effortType,
    speciesName
  );
  if (!rows.length) return null;

  const isFykeOrGill = effortType === "fyke_net" || effortType === "gillnet";
  const headers = isSeine
    ? ["Haul #", LABEL.NUM, "CPUE"]
    : isFykeOrGill
      ? ["Net", "Number of Nights", LABEL.NUM, "CPUE"]
      : ["Net", LABEL.NUM, "CPUE"];

  const bodyRows = rows.map((row) => {
    if (isSeine) return [row.label, String(row.fishCount), String(row.cpue)];
    if (isFykeOrGill) {
      return [
        row.label,
        String(row.effortLabel),
        String(row.fishCount),
        String(row.cpue),
      ];
    }
    return [row.label, String(row.fishCount), String(row.cpue)];
  });

  const footerRow = isSeine
    ? ["Average CPUE", String(totalFish), String(avgCpue)]
    : isFykeOrGill
      ? ["Average CPUE", String(totalEffort), String(totalFish), String(avgCpue)]
      : ["Average CPUE", String(totalFish), String(avgCpue)];

  return {
    title: title || cpueSessionTitle(effortType),
    headers,
    bodyRows,
    footerRow,
  };
}

/** Session-level CPUE table for PDF/XLSX export (null when not applicable). */
export function getSessionCpueExportBlock(detail) {
  const method = detail?.SamplingMethod;
  const effortUnits = detail?.EffortUnits || [];
  const samplingData = detail?.SamplingData || [];
  const effortType = effortTypeForSamplingMethod(method);

  if (!effortType || !effortUnits.length) return null;

  if (effortType === "electrofishing_area") {
    return buildElectrofishingExportBlock(effortUnits, samplingData);
  }

  return buildNetCpueExportBlock(effortUnits, samplingData, effortType);
}

/** Per-species CPUE tables for PDF/XLSX export. */
export function getPerSpeciesCpueExportBlocks(detail, fishTypeData) {
  const method = detail?.SamplingMethod;
  const effortUnits = detail?.EffortUnits || [];
  const samplingData = detail?.SamplingData || [];
  const effortType = effortTypeForSamplingMethod(method);

  if (!effortType || !effortUnits.length || !fishTypeData?.length) return [];

  const speciesWithFish = fishTypeData.filter((s) => Number(s.NumberOfFishes) > 0);
  if (!speciesWithFish.length) return [];

  return speciesWithFish
    .map((summary) => {
      const speciesName = summary.FishTypeName || "Unknown";
      if (effortType === "electrofishing_area") {
        return buildElectrofishingExportBlock(
          effortUnits,
          samplingData,
          speciesName,
          speciesName
        );
      }
      return buildNetCpueExportBlock(
        effortUnits,
        samplingData,
        effortType,
        speciesName,
        speciesName
      );
    })
    .filter(Boolean);
}

/** Append CPUE blocks to an array-of-arrays sheet (XLSX). */
export function appendCpueExportRows(sheetData, detail) {
  const sessionBlock = getSessionCpueExportBlock(detail);
  if (sessionBlock) {
    sheetData.push([sessionBlock.title]);
    sheetData.push(sessionBlock.headers);
    sessionBlock.bodyRows.forEach((row) => sheetData.push(row));
    sheetData.push(sessionBlock.footerRow);
    sheetData.push([""]);
  }

  const speciesBlocks = getPerSpeciesCpueExportBlocks(detail, detail?.FishType);
  if (speciesBlocks.length > 0) {
    sheetData.push(["CPUE by Species"]);
    speciesBlocks.forEach((block) => {
      sheetData.push([block.title]);
      sheetData.push(block.headers);
      block.bodyRows.forEach((row) => sheetData.push(row));
      sheetData.push(block.footerRow);
      sheetData.push([""]);
    });
  }
}

export { formatHMS };
